import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { RequireAccess } from '../components/RequireAccess';
import { LotCard } from '../components/LotCard';
import { Skeleton } from '../components/Skeleton';
import { EmptyState } from '../components/EmptyState';
import { DotRule } from '../components/DotRule';
import { Button, Container, Heading, Stack, Text } from '../components/ui';
import { useSession } from '../lib/session';
import { fetchWatchlist } from '../lib/queries';
import type { Post } from '../lib/types';
import './Watchlist.css';

/**
 * /watchlist - members-only list of posts the viewer is watching.
 *
 * V1 = auction-only (the watch button is only surfaced on auction lots),
 * so every card is a LotCard. Defensive fallback for the edge case where
 * a non-auction post somehow ends up watched: render a basic title-only
 * link instead of crashing inside LotCard.
 *
 * Sort already happens in fetchWatchlist (live > scheduled > ended,
 * each tier ordered sensibly).
 */
export default function Watchlist() {
  return (
    <RequireAccess>
      <WatchlistInner />
    </RequireAccess>
  );
}

function WatchlistInner() {
  const { profile } = useSession();
  const [posts, setPosts] = useState<Post[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!profile) return;
    const ctrl = new AbortController();
    (async () => {
      try {
        const data = await fetchWatchlist(profile.id, ctrl.signal);
        if (ctrl.signal.aborted) return;
        setPosts(data);
      } catch (err: any) {
        if (ctrl.signal.aborted || err?.name === 'AbortError') return;
        console.error('[watchlist] fetch failed', err);
        setError(err?.message ?? 'Something broke loading your favorites.');
      } finally {
        if (!ctrl.signal.aborted) setLoading(false);
      }
    })();
    return () => ctrl.abort();
  }, [profile?.id]);

  return (
    <div data-ds className="watchlist">
      <header className="watchlist__header">
        <Container size="lg">
          {/* Eyebrow + headline share the canonical Stack gap="tight" lockup so
              the eyebrow-to-title rhythm matches every other page (Saleroom etc.). */}
          <Stack gap="tight">
            <p className="kicker">Your Favorites</p>
            <Heading level="h1" className="watchlist__headline">The lots you've saved.</Heading>
          </Stack>
          <DotRule className="watchlist__rule" />
        </Container>
      </header>

      <Container size="lg">
        {loading ? (
          <div className="lot-grid" aria-busy="true" aria-label="Loading your favorites">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="watchlist__skel-cell">
                <Skeleton variant="card" aspectRatio={4 / 5} />
                <div className="watchlist__skel-meta">
                  <Skeleton variant="line" width="70%" height={18} />
                  <Skeleton variant="line" width="45%" height={12} />
                </div>
              </div>
            ))}
          </div>
        ) : error ? (
          <EmptyState
            eyebrow="Error"
            headline="We couldn't load your favorites."
            body={error}
          />
        ) : posts.length === 0 ? (
          <EmptyState
            eyebrow="Favorites"
            headline="You haven't favorited anything yet."
            body="Tap the heart on any lot to save it here."
            halftone="cloud"
            cta={
              <Button as={Link} to="/sales-room" variant="accent">
                Browse the Saleroom
              </Button>
            }
          />
        ) : (
          <div className="lot-grid">
            {posts.map((p) =>
              p.auction ? (
                <LotCard key={p.id} post={p} />
              ) : (
                // Defensive fallback for watched non-auction posts. V1
                // only surfaces the watch button on auctions, but the
                // post_watchers table is generic - if a future feature
                // adds watch support elsewhere these would otherwise
                // crash the LotCard hero. Keep them as a quiet link.
                <Link
                  key={p.id}
                  to={`/p/${p.slug}`}
                  className="watchlist__fallback"
                  aria-label={p.title}
                >
                  <Heading level="h3" className="watchlist__fallback-title">{p.title}</Heading>
                  {p.dek && <Text role="small" muted className="watchlist__fallback-dek">{p.dek}</Text>}
                </Link>
              ),
            )}
          </div>
        )}
      </Container>
    </div>
  );
}
